/**
 * SectionItem.js
 */

import React, {useState, useEffect, useContext, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {imageIcFilterNone24px, actionIcBuild24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {labels} from '../../../../util/utils';
import EllipseControl from '../../EllipsesControl/EllipsesControl';
import FormBodyContainer from './../Bodies/FormBodyContainer';
import env from '../../../../util/env';
import {deleteSection, duplicateSection} from '../../../../actions/FormData/formdataactions';
import {clientSideEventNames, serviceURL, documentTypes, entities} from '../../../../util/uiconstants';
import Utility from '../../../../util/utilityfunctions';
import {useClearAllComments, useDocumentId, useEngagementId} from '../../../../util/customHooks';
import Validation from '../../CanvasForm/Common/Validation';
import SectionOptions from './SectionOptions';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import SectionRelatedObjects from './SectionRelatedObjects';
import {DocumentTypeIdContext} from '../../../Main/Forms/CanvasFormContainer';
import CompareSnapshotLabel from '../CompareSnapshotLabel';
import {setIsModalOpen} from '../../../../actions/setismodalopen';
import {clearFormBodySaveState} from '../../../../actions/Form/formBodyActions';
import Button from '../../BaseComponentWrappers/MotifButton';
import Icon from '../../BaseComponentWrappers/MotifIcon';
import {IconoirMinus, IconoirPlus} from '@ey-xd/motif-icon';

export default function SectionItem(props) {
	const {
		bodyOnlyView,
		formData,
		section,
		headerId,
		isExpanded,
		selectedSectionId,
		setSelectedSection,
		forceReload,
		isSnapshotView,
		changesView,
		commentsCleared,
		onNext,
		onPrevious,
		nextName,
		previousName
	} = props;

	const sectionOptionsActions = {
		delete: 'delete',
		duplicate: 'duplicate',
		edit: 'edit'
	};
	const isExpandable = !bodyOnlyView;

	const sectionName =
			section?.customLabel && true && section?.customLabel.trim().length > 0
				? section?.name + ' - ' + section?.customLabel
				: section?.name,
		showIsCustom = section?.isCustom || (section?.isCustom && section?.isDuplicate);

	const [showModal, setShowModal] = useState(false),
		[isLoading, setIsLoading] = useState(false),
		[scroll, setScroll] = useState(false),
		[clearedComments, setClearedComments] = useState(commentsCleared),
		[showOverlayLoader, setShowOverlayLoader] = useState(false),
		[selected, setSelected] = useState(selectedSectionId),
		[thisSection, setThisSection] = useState(section),
		[sectionExpanded, setSectionExpanded] = useState(isExpanded);

	const sectionRef = useRef(section?.id);

	const signOffs = useSelector((store) => store.signOffs);

	const dispatch = useDispatch();

	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		documentTypeId = useContext(DocumentTypeIdContext);

	const {setClearAllComments} = useClearAllComments(
		engagementId,
		documentId,
		documentTypeId,
		entities.FormSection,
		section?.id,
		() => {
			setIsLoading(false);
			setClearAllComments(false);
			setShowOverlayLoader(false);
			setClearedComments(true);
		}
	);

	const saveResponse = (saveType) => {
		dispatch(setIsModalOpen(true));
		setIsLoading(true);
		let promise = dispatch(clearFormBodySaveState());
		switch (saveType) {
			case sectionOptionsActions.duplicate:
				promise = promise.then(
					dispatch(
						duplicateSection(
							env.getBaseURL(serviceURL, engagementId) +
								`forms/${documentId}/headers/${headerId}/sections/${thisSection.id}/Duplicate`,
							{documentId: documentId, headerId: headerId, sectionId: thisSection.id, unitType: 'section'},
							thisSection.id
						)
					)
						.then(() => {
							window.dispatchEvent(
								new CustomEvent(clientSideEventNames.onCreateOrEdit, {
									detail: {
										selectedId: headerId,
										signOffs: signOffs,
										sectionId: thisSection.id,
										engagement: {engagementId: engagementId}
									}
								})
							);
						})
						.finally(() => {
							setIsLoading(false);
							dispatch(setIsModalOpen(false));
						})
				);
				break;
			case sectionOptionsActions.delete:
				setSelectedSection(undefined);
				setSelected(undefined);
				setThisSection(undefined);
				promise = promise.then(
					dispatch(
						deleteSection(env.getBaseURL(serviceURL, engagementId) + `Forms/${documentId}/Sections/${thisSection.id}`, {
							documentId: documentId,
							headerId: headerId,
							sectionId: thisSection.id
						})
					)
						.then(() => {
							window.dispatchEvent(
								new CustomEvent(clientSideEventNames.onCreateOrEdit, {
									detail: {selectedId: undefined, signOffs: signOffs, engagement: {engagementId: engagementId}}
								})
							);
						})
						.finally(() => {
							setIsLoading(false);
							dispatch(setIsModalOpen(false));
						})
				);
				break;
		}
	};

	const expandCollapse = (evt) => {
		if (!isExpandable) {
			evt.preventDefault();
			return;
		}

		setSectionExpanded(!sectionExpanded);
		setSelectedSection(thisSection);
	};

	useEffect(() => {
		setScroll(false);
	}, [formData]);

	useEffect(() => {
		if (thisSection?.id === selected) {
			setScroll(true);
			setSectionExpanded(isExpanded);
		}
	}, [isExpanded, selected]);

	useEffect(() => {
		setClearedComments(commentsCleared);
	}, [commentsCleared]);

	useEffect(() => {
		setThisSection(section);
	}, [section]);

	return (
		<>
			<LoadingIndicator show={showOverlayLoader} styleName="independenceLoader" />
			{isLoading ? (
				<LoadingIndicator view="inlineView" styleName="relatedDocuments-loading" />
			) : (
				<>
					{thisSection ? (
						<Wrapper className={`SectionBodyAccordion ${bodyOnlyView === true ? 'bodyOnlyView customScrollbar' : ''}`}>
							<section ref={sectionRef} className={`accordionWrapper ${sectionExpanded ? 'selected-accordion' : ''}`}>
								<section className="section-container">
									<section
										className={`section-Wrapper${isExpandable ? '' : ' no-link'}`}
										id={thisSection?.id}
										onClick={expandCollapse}
									>
										{!bodyOnlyView && (
											<section className="plusminusIcons" title={!sectionExpanded ? labels.expand : labels.collapse}>
												<Icon
													className={`medium`}
													src={!sectionExpanded ? IconoirPlus : IconoirMinus}
													title={!sectionExpanded ? labels.expand : labels.collapse}
												/>
											</section>
										)}
										<section className="sectionName">
											<EllipseControl
												content={Utility.getHtmlTextElement(sectionName, isSnapshotView)}
												tooltip={Utility.getHtmlTextElement(sectionName, isSnapshotView)}
												tooltipClass="sectionNameEllipses"
												displayId={''}
												noOfLines={1}
											/>
										</section>
									</section>
									<section className="sectionRightSideContainer">
										{!isSnapshotView ? (
											<section className="section-validation">
												<Validation
													documentId={documentId}
													headerId={headerId}
													sectionId={thisSection?.id}
													displayFor={'section'}
													clearComments={() => {
														setShowOverlayLoader(true);
														setIsLoading(true);
														setClearAllComments(true);
													}}
												/>
											</section>
										) : (
											<CompareSnapshotLabel snapshotObject={thisSection} />
										)}
										<section className="sectionOptions">
											<section className="icon-wrapper">
												<span
													className={`section-edit-icon ${
														!showIsCustom && thisSection?.isDuplicate ? 'showIcon' : 'hiddenIcon'
													}`}
												>
													<Icon src={imageIcFilterNone24px} title={labels.duplicate} />
												</span>
												<span
													title={labels.custom}
													className={`section-edit-icon ${showIsCustom ? 'showIcon' : 'hiddenIcon'}`}
												>
													<Icon src={actionIcBuild24px} title={labels.custom} />
												</span>
												{!isSnapshotView &&
													documentTypeId !== documentTypes.INDEPENDENCEFORMINDIVIDUAL &&
													documentTypeId !== documentTypes.INDEPENDENCEFORMTEMPLATE && (
														<span className="section-options">
															<SectionOptions
																id={thisSection?.id}
																formData={formData}
																headerId={headerId}
																className="sectionOptions"
																show={showModal}
																isCustom={thisSection?.isCustom}
																sectionName={sectionName}
																sectionNameNoCustomLabel={thisSection?.name}
																saveResponse={saveResponse}
																sectionCustomLabel={thisSection?.customLabel}
																isDuplicate={thisSection?.isDuplicate}
																documentTypeId={documentTypeId}
																sectionOptionsActions={sectionOptionsActions}
															/>
														</span>
													)}
											</section>
										</section>
									</section>
								</section>
							</section>
							{sectionExpanded && (
								<>
									{thisSection && thisSection.hasRelatedObjects === true && (
										<SectionRelatedObjects
											isSnapshotView={isSnapshotView}
											formSectionRelatedObjects={isSnapshotView ? thisSection?.formSectionRelatedObjects : undefined}
											sectionId={thisSection?.id}
											section={thisSection}
										/>
									)}
									<FormBodyContainer
										key={thisSection?.id}
										bodyOnlyView={bodyOnlyView}
										isSnapshotView={isSnapshotView}
										commentsCleared={clearedComments}
										forceReload={forceReload}
										sectionExpanded={sectionExpanded}
										sectionId={thisSection?.id}
										headerId={headerId}
										selectedSectionId={selected ? selected : thisSection?.id}
										snapShotbodies={isSnapshotView ? thisSection?.bodies : undefined}
										sectionRef={sectionRef}
										changesView={changesView}
										scroll={scroll}
										setScroll={setScroll}
									/>
								</>
							)}
						</Wrapper>
					) : (
						<section className={`emptyContainer ${bodyOnlyView === true ? 'bodyOnlyView' : ''}`}>
							{labels.noSectionsAvailable}
						</section>
					)}
					{bodyOnlyView && (
						<BodyOnlyFooter className="BodyOnlyFooter">
							<section className="LeftButton">
								{previousName !== null && (
									<>
										<Button
											onClick={() => {
												onPrevious(thisSection?.id);
											}}
											variant="secondary"
											title={labels.previous}
											style={{minWidth: 'var(--px-40)'}}
											stepper="prev"
										/>
										<span className="LeftButtonTextWrapper">
											<EllipseControl
												content={labels.previous}
												tooltip={labels.previous}
												noOfLines={1}
												ellipsisClass="TitleSpan"
											/>
											<EllipseControl
												content={previousName}
												tooltip={previousName}
												noOfLines={1}
												ellipsisClass="TitleSection"
											/>
										</span>
									</>
								)}
							</section>
							{nextName !== null && (
								<section className="RightButton">
									<span className="RightButtonTextWrapper">
										<EllipseControl
											content={labels.next}
											tooltip={labels.next}
											noOfLines={1}
											ellipsisClass="TitleSpan"
										/>
										<EllipseControl content={nextName} tooltip={nextName} noOfLines={1} ellipsisClass="TitleSection" />
									</span>
									<Button
										onClick={() => {
											onNext(thisSection?.id);
										}}
										variant="secondary"
										title={labels.next}
										style={{minWidth: 'var(--px-40)'}}
										stepper="next"
									/>
								</section>
							)}
						</BodyOnlyFooter>
					)}
				</>
			)}
		</>
	);
}

const BodyOnlyFooter = styled.section`
	display: flex;
	padding: var(--px-10) var(--px-40) var(--px-10) var(--px-20);
	width: 100%;
	border-top: var(--px-1) solid var(--neutrals-300);
	z-index: 10;
	height: var(--px-50);
	.LeftButton {
		width: 50%;
		height: var(--px-30);
		display: flex;
		justify-content: flex-start;
		.LeftButtonTextWrapper {
			display: flex;
			flex-direction: column;
			width: auto;
			max-width: 90%;
			margin-left: var(--px-10);
			.ellipses {
				justify-content: left;
			}
			.TitleSpan {
				font-size: var(--px-10);
				font-weight: 300;
				padding-bottom: var(--px-4);
				height: var(--px-12);
				line-height: var(--px-12);
			}
			.TitleSection {
				font-size: var(--px-14);
				font-weight: normal;
				height: var(--px-16);
				line-height: var(--px-16);
			}
		}
		.motif-button {
			min-width: var(--px-30) !important;
			width: var(--px-30) !important;
			.motif-icon {
				margin: 0;
			}
		}
	}
	.RightButton {
		width: 50%;
		height: var(--px-30);
		display: flex;
		justify-content: flex-end;
		.RightButtonTextWrapper {
			display: flex;
			flex-direction: column;
			width: auto;
			max-width: 90%;
			margin-right: var(--px-10);
			.ellipses {
				justify-content: right;
			}
			.TitleSpan {
				font-size: var(--px-10);
				font-weight: 300;
				padding-bottom: var(--px-4);
				height: var(--px-12);
				line-height: var(--px-12);
			}
			.TitleSection {
				font-size: var(--px-14);
				font-weight: normal;
				height: var(--px-16);
				line-height: var(--px-16);
			}
		}
		.motif-button {
			min-width: var(--px-30) !important;
			width: var(--px-30) !important;
			margin-right: var(--px-5);
			.motif-icon {
				margin: 0;
			}
		}
	}
`;

const Wrapper = styled.section`
	display: block;
	margin-bottom: var(--px-5);
	width: 100%;
	padding: var(--px-20) var(--px-40) 0 0;
	&:last-child {
		margin-bottom: var(--px-10);
	}
	.no-link {
		cursor: default !important;
	}
	.FormBodyLoader {
		display: flex;
		width: 100%;
		justify-content: center;
	}
	.accordionWrapper {
		display: block;
		width: 100%;
		padding: var(--px-10) 0 var(--px-20) var(--px-10);
		.section-container {
			display: flex;
			width: 100%;
		}
		.section-Wrapper {
			display: flex;
			width: calc(100% - var(--px-145));
			cursor: pointer;
			padding-right: var(--px-10);
			.plusminusIcons {
				display: flex;
				align-items: center;
				transition: 0.5s;
				transition-property: height;
				.motif-icon {
					display: flex;
					align-items: center;
				}
				svg {
					color: var(--neutrals-900);
				}
			}
			.sectionName {
				display: flex;
				width: calc(100% - var(--px-40));
				max-width: 95vw;
				align-items: center;
				font-size: var(--px-24);
				font-weight: 400;
				line-height: var(--px-32);
				margin-left: var(--px-10);
				color: var(--neutrals-900);
				.diffins {
					background-color: #e6ffe6;
					font-family: inherit;
					text-decoration: none;
				}
				.diffdel {
					color: #999;
					background-color: #ffe6e6;
					font-family: inherit;
				}
				.mod {
					background-color: #ffe1ac;
					font-family: inherit;
				}
			}
		}
		.sectionRightSideContainer {
			display: flex;
			width: var(--px-220);
			justify-content: flex-end;
		}
		.section-validation {
			width: var(--px-65);
			margin-right: var(--px-16);
			padding-left: var(--px-10);
			.dropdownValidationName {
				display: inline-flex;
				width: calc(100% - var(--px-50));
				font-size: var(--px-12);
				margin-right: var(--px-10);
				padding-left: var(--px-2);
			}
			.dropdownValidationCount {
				display: inline-flex;
				width: var(--px-40);
				font-size: var(--px-12);
				justify-content: flex-end;
				color: #b9251c;
				.ellipses {
					justify-content: right;
				}
			}
		}
		.sectionOptions {
			display: inline-flex;
			justify-content: flex-end;
			width: var(--px-95);
			svg {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				padding: 0;
			}
			.icon-wrapper {
				display: flex;
				margin: 0;
				align-items: center;
				.section-edit-icon {
					margin-right: var(--px-10);
					height: var(--px-20);
					svg {
						padding: 0;
						cursor: default;
					}
				}
				.hiddenIcon {
					display: none;
				}
				.showIcon {
					display: inline-block;
				}
				.section-options {
					.motif-dropdown {
						display: flex;
						position: relative;
						margin: 0;
						.motif-button {
							display: flex;
						}
					}
				}
			}
		}
	}
	.section-body {
		display: flex;
		justify-content: center;
		font-size: var(--px-14);
		width: 100%;
		border-bottom: var(--px-1) solid var(--neutrals-200);
		padding: var(--px-10) 0 var(--px-10) var(--px-10);
		position: relative;
		.section-edit-icon {
			display: flex;
			height: var(--px-24);
			width: var(--px-24);
			margin: var(--px-15) 0 0 var(--px-10);
			justify-content: center;
			align-items: center;
		}
		.section-container {
			& + .DraggableGuidance {
				position: fixed;
				top: var(--px-66);
				left: 16%;
				@media only screen and (min-width: 1747px) and (max-width: 1920px) {
					left: 12%;
				}
				@media only screen and (min-width: 1537px) and (max-width: 1746px) {
					left: 10%;
				}
				@media only screen and (min-width: 1401px) and (max-width: 1536px) {
					left: 9%;
				}
				@media only screen and (min-width: 1280px) and (max-width: 1366px) {
					left: 7%;
				}
			}
		}
	}
`;
